import { isOneClick } from '@cast/constants';
import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { parseHttpUrl } from '@cast/utils';

/**
 * Legacy main-console hosts (not OneClick `app.console…` / review hosts).
 * Matches console.cast.ai, console.eu.cast.ai, console.dev-master.cast.ai,
 * console.india.cast.ai, etc.
 */
const isLegacyConsoleHostname = (hostname: string): boolean =>
  /^console\.(?:[a-z0-9-]+\.)*cast\.ai$/i.test(hostname);

const AUTOMATION_PREFIX = PRODUCT_BASE_PATHS.automation;

/**
 * When running OneClick, rewrite absolute legacy console deep links to the
 * current origin and canonical automation cluster paths.
 */
export const rewriteLegacyConsoleUrl = (url: string): string => {
  if (!url || !isOneClick()) {
    return url;
  }

  const parsed = parseHttpUrl(url);
  if (!parsed || !isLegacyConsoleHostname(parsed.hostname)) {
    return url;
  }

  let pathname = parsed.pathname;
  if (
    pathname.startsWith('/external-clusters/') &&
    !pathname.startsWith(`${AUTOMATION_PREFIX}/`)
  ) {
    pathname = `${AUTOMATION_PREFIX}${pathname}`;
  }

  return `${window.location.origin}${pathname}${parsed.search}${parsed.hash}`;
};

/**
 * Recursively rewrite legacy console URL string values in notification details JSON.
 */
export const rewriteLegacyConsoleUrlsInDetails = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return rewriteLegacyConsoleUrl(value);
  }
  if (Array.isArray(value)) {
    return value.map(rewriteLegacyConsoleUrlsInDetails);
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [
        key,
        rewriteLegacyConsoleUrlsInDetails(nested),
      ])
    );
  }
  return value;
};
